import React, { Fragment, useEffect, useRef, useState } from "react";
import useUsers from "containers/user-management/useHooks";
import DataNotFound from "components/particles/table/data-not-found";
import Pagination from "components/particles/table/pagination";
import { useSelector } from "react-redux";

interface Props {
  eventId: number;
}

const Community: React.FC<Props> = ({ eventId }) => {
  const { isLoading } = useSelector((state: any) => state.sharedReducer);
  const [messages, setMessages] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    count: 0,
    currentPage: 0,
    totalPages: 1,
  });
  const { getCommunityMessageById } = useUsers();

  const pageSize = 10;
  const containerRef = useRef<HTMLDivElement>(null);
  const prevScrollHeight = useRef(0);

  const loadMessages = (page: number) => {
    const container = containerRef.current;
    if (page > 0 && container) {
      prevScrollHeight.current = container.scrollHeight;
    }
    getCommunityMessageById(
      setMessages,
      eventId,
      setPagination,
      page,
      pageSize
    );
  };
  useEffect(() => {
    setMessages([]);
    loadMessages(0);
  }, [eventId]);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    if (pagination.currentPage === 0) {
      container.scrollTop = container.scrollHeight;
    } else {
      const newHeight = container.scrollHeight;
      const diff = newHeight - prevScrollHeight.current;
      container.scrollTop = diff;
    }
  }, [messages, pagination.currentPage]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop } = e.currentTarget;
    if (scrollTop <= 50 && pagination.currentPage + 1 < pagination.totalPages) {
      loadMessages(pagination.currentPage + 1);
    }
  };

  const formatDateHeading = (dateStr: string): string => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const isSameDay = (d1: Date, d2: Date) =>
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate();

    if (isSameDay(date, today)) return "Today";
    if (isSameDay(date, yesterday)) return "Yesterday";

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (dateStr: string): string =>
    new Date(dateStr).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });

  // for date headings
  let lastShownDate: string | null = null;

  return (
    <div>
      <div
        ref={containerRef}
        className="py-6 overflow-y-auto max-h-[500px]"
        onScroll={handleScroll}
      >
        {messages.map((msg) => {
          const msgDate = new Date(msg.createdAt).toDateString();
          const showHeading = msgDate !== lastShownDate;
          lastShownDate = msgDate;

          return (
            <React.Fragment key={msg.id}>
              {showHeading && (
                <div className="text-center text-xs text-uiGrayText my-4">
                  {formatDateHeading(msg.createdAt)}
                </div>
              )}
              <div className="mb-5">
                <div className="flex items-start gap-3 sm:gap-4">
                  <img
                    src={msg.userProfilePic}
                    alt="Profile"
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex-shrink-0"
                  />
                  <div className="bg-culturedBg rounded-tl-none rounded-lg p-3 sm:px-4 max-w-full sm:max-w-2xl w-fit">
                    <div className="font-semibold text-sm text-primaryText mb-1">
                      {msg.userName}
                    </div>
                    <div className="text-sm text-primaryText whitespace-pre-line break-words">
                      {msg.message}
                    </div>
                    <div className="text-xs text-uiGrayText mt-1">
                      {formatTime(msg.createdAt)}
                    </div>
                  </div>
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>
      <Fragment>
        <DataNotFound show={!isLoading && !messages.length} />
      </Fragment>
    </div>
  );
};

export default Community;
